import React, { useState } from 'react';
import { Input } from 'reactstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { confirmSignIn } from 'aws-amplify/auth';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import { authActions } from 'redux/auth';
import { store } from 'redux/store';
import StyledReactButton from 'components/Buttons/StyledButton';
import ErrorMessage from './ErrorMessage';
import { AuthError } from '../models';

const RequireNewPassword: React.FC = () => {
  const { t } = useTranslation(['signin', 'formError']);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [passwordChangeParams, setPasswordChangeParams] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showInput, setShowInput] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{
    code?: string;
    message?: string;
  }>({});

  const onClickCheckbox = () => {
    setShowInput(showInput => !showInput);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name: key, value } = e.target;
    setValue(key, value);
    setPasswordChangeParams(prevState => ({ ...prevState, [key]: value }));
  };

  const onClickBack = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    store.dispatch(authActions.newPassword(false));
  };

  const onSubmit = (): void => {
    setErrorInfo({});
    confirmSignIn({ challengeResponse: passwordChangeParams.password })
      .then(() => {
        store.dispatch(authActions.login());
      })
      .catch(error => {
        const errorCode = error instanceof Error ? error.name : '';
        let errorMessage = error instanceof Error ? error.message : '';
        switch (errorCode) {
          case AuthError.notAuthorizedException:
            // user削除、session timeout など
            errorMessage = t('newPasswordNotAuthorizedException');
            break;
          case AuthError.invalidPasswordException:
            errorMessage = t('invalidPasswordException');
            break;
        }
        setErrorInfo({
          code: errorCode,
          message: errorMessage,
        });
      });
  };

  const { message: errorMessage } = errorInfo;
  const { password, confirmPassword } = passwordChangeParams;
  return (
    <Container>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <StyledTitle>{t('signin:titleRequireNewPassword')}</StyledTitle>
        <StyledPasswordInput>
          <PasswordLabel>{t('signin:newPassword')}</PasswordLabel>
          <div>
            <StyledInput
              {...register('password', {
                required: true,
              })}
              type={showInput ? 'text' : 'password'}
              value={password}
              onChange={onChange}
            />
          </div>
        </StyledPasswordInput>
        <StyledCheckbox>
          <CheckboxLabel>
            <Input
              type={'checkbox'}
              defaultChecked={showInput}
              onClick={onClickCheckbox}
            />
            {t('signin:checkPassword')}
          </CheckboxLabel>
        </StyledCheckbox>
        <StyledSubmitButton>
          <StyledReactButton disabled={password === ''} color="primary" block>
            {t('signin:newPasswordButton')}
          </StyledReactButton>
        </StyledSubmitButton>
        {(!_.isEmpty(errors) ||
          errorMessage !== null ||
          password !== confirmPassword) && (
          <ErrorMessage errors={errors} errorMessage={errorMessage} />
        )}
        <StyledAnchor href={'#'} onClick={onClickBack}>
          {t('signin:back')}
        </StyledAnchor>
      </StyledForm>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 95vh;
`;

const StyledForm = styled.form`
  width: 330px;
  text-align: -webkit-center;
`;

const StyledTitle = styled.p`
  margin-bottom: 15px;
  font-size: 2rem;
`;

const StyledInput = styled(Input)`
  width: 200px;
  margin-bottom: 8px;
`;

const StyledSubmitButton = styled.div`
  width: 280px;
  margin-bottom: 20px;
`;

const StyledPasswordInput = styled.div`
  display: flex;
  align-items: center;
`;

const PasswordLabel = styled.div`
  width: 220px;
  font-size: 0.95rem;
`;

const StyledCheckbox = styled.div`
  margin: 0 10px 20px 0;
  text-align: right;
`;

const CheckboxLabel = styled.label`
  padding-left: 1.25rem;
  > input {
    margin-top: 0.4rem;
  }
`;

const StyledAnchor = styled.a`
  :hover {
    text-decoration: underline;
  }
`;

export default RequireNewPassword;
